import { useTranslation } from "react-i18next";
import { useGetTarif, useGetTarifs } from "@/api/tarif.api";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { ChevronRight, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { ReassignCompanyFormData, ReassignCompanySchema } from "./tarifForm";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUpdateCompanyBoV1 } from "@/api/company.api";
import { CustomInput } from "@/components/ui/customs/input";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";

interface TarifReassignCompanyModalProps {
  open: boolean;
  closeModal: () => void;
  tarifId: string;
}

export const TarifReassignCompanyModal = ({
  open,
  closeModal,
  tarifId,
}: TarifReassignCompanyModalProps) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  const handleClose = () => {
    setSelectedCompanyId(null);
    closeModal();
  };

  if (selectedCompanyId) {
    return (
      <ReassignForm
        open={open}
        companyId={selectedCompanyId}
        currentTarifId={tarifId}
        onBack={() => setSelectedCompanyId(null)}
        onClose={handleClose}
      />
    );
  }

  return (
    <CompanyListView
      open={open}
      tarifId={tarifId}
      onSelectCompany={setSelectedCompanyId}
      onClose={handleClose}
    />
  );
};

const Spinner = () => <Loader2 className="h-4 w-4 animate-spin" />;

interface CompanyListViewProps {
  open: boolean;
  tarifId: string;
  onSelectCompany: (companyId: string) => void;
  onClose: () => void;
}

const CompanyListView = ({ open, tarifId, onSelectCompany, onClose }: CompanyListViewProps) => {
  const { t } = useTranslation(["boParameters", "common"]);
  const { data: tarif, isPending } = useGetTarif(tarifId);
  const [search, setSearch] = useState("");

  const filteredCompanies = useMemo(() => {
    if (!tarif) return [];
    const normalized = search.trim().toLowerCase();
    if (!normalized) return tarif.companies;
    return tarif.companies.filter((company) =>
      company.businessName.toLowerCase().includes(normalized),
    );
  }, [tarif, search]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("tarifReassignModal.listTitle")}
      size="lg"
      contentClassName="max-h-2/3"
      footer={
        <Button variant="outline" onClick={onClose} className="w-full">
          {t("common:actions.close")}
        </Button>
      }
    >
      {isPending ? (
        <div className="flex items-center justify-center">
          <Spinner />
        </div>
      ) : !tarif || tarif.companies.length === 0 ? (
        <div className="flex items-center justify-center">
          <p>{t("tarifReassignModal.empty")}</p>
        </div>
      ) : (
        <div className="flex w-full min-w-0 flex-col gap-2 overflow-x-auto">
          <div className="flex w-full flex-col gap-2 p-1">
            <CustomInput
              value={search}
              onChange={(value) => setSearch(value?.toString() ?? "")}
              placeholder={t("tarifReassignModal.searchPlaceholder")}
              inputType="text"
            />
            <p className="text-muted-foreground text-sm">{t("tarifReassignModal.listHelp")}</p>
            {filteredCompanies.length === 0 ? (
              <p className="text-muted-foreground text-center text-sm">
                {t("tarifReassignModal.noMatch")}
              </p>
            ) : (
              filteredCompanies.map((company) => (
                <Button
                  key={company.id}
                  variant="outline"
                  className="flex w-full min-w-0"
                  onClick={() => onSelectCompany(company.id)}
                >
                  <div className="flex w-full min-w-0 items-center justify-between gap-2">
                    <p className="min-w-0 flex-1 truncate text-left">{company.businessName}</p>
                    <ChevronRight className="shrink-0" />
                  </div>
                </Button>
              ))
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

interface ReassignFormProps {
  open: boolean;
  companyId: string;
  currentTarifId: string;
  onBack: () => void;
  onClose: () => void;
}

// Hard cap on the tarifs fetched for the reassignment dropdown. Replace with a
// server-side searchable autocomplete if the catalogue grows past this number.
const REASSIGN_TARIFS_PAGE_SIZE = 1000;

const ReassignForm = ({ open, companyId, currentTarifId, onBack, onClose }: ReassignFormProps) => {
  const { t } = useTranslation(["boParameters", "common"]);
  const { data: tarif } = useGetTarif(currentTarifId);
  const { data: tarifs } = useGetTarifs({ take: REASSIGN_TARIFS_PAGE_SIZE });
  const { mutate: updateCompany, isPending: isSubmitting } = useUpdateCompanyBoV1();

  const company = useMemo(
    () => tarif?.companies.find((c) => c.id === companyId),
    [tarif, companyId],
  );

  const tarifOptions = useMemo(
    () =>
      tarifs?.data
        .filter((option) => option.id !== currentTarifId && !option.isExpress)
        .map((option) => ({ label: option.label, value: option.id })) ?? [],
    [tarifs, currentTarifId],
  );

  const form = useForm<ReassignCompanyFormData>({
    resolver: zodResolver(ReassignCompanySchema),
    defaultValues: { tarifId: "" },
  });

  const selectedTarifId = form.watch("tarifId");

  const onSubmit = form.handleSubmit(({ tarifId: nextTarifId }) => {
    updateCompany(
      { id: companyId, payload: { tarifId: nextTarifId } },
      {
        onSuccess: () => {
          form.reset();
          onClose();
        },
      },
    );
  });

  const title = company
    ? t("tarifReassignModal.formTitle", { businessName: company.businessName })
    : t("tarifReassignModal.formTitleFallback");

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button
            type="submit"
            form="reassign-company-form"
            className="w-full"
            disabled={isSubmitting || !selectedTarifId}
          >
            {isSubmitting ? <Spinner /> : t("tarifReassignModal.submit")}
          </Button>
          <Button variant="outline" onClick={onBack} className="w-full">
            {t("common:actions.back")}
          </Button>
        </>
      }
    >
      <form id="reassign-company-form" onSubmit={onSubmit}>
        <ControlledSelectV2
          name="tarifId"
          control={form.control}
          topLabel={t("tarifReassignModal.tarifLabel")}
          placeholder={t("tarifReassignModal.tarifPlaceholder")}
          contentClassName="w-full"
          options={tarifOptions}
        />
      </form>
    </Modal>
  );
};
